import React, { useState, useEffect } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import { Button } from "../../../components/ui";

export default function ProductModal({ 
  open, 
  onClose, 
  product, 
  onSave,
  onDelete,
  submitting,
  deleting
}) {
  const [formData, setFormData] = useState({
    name: "",
    brand: "",
    commission_rate: "",
    sizes: [""],
    size_prices: {}
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (product) {
      // Editing existing product
      setFormData({
        name: product.name || "",
        brand: product.brand || "",
        commission_rate: product.commission_rate ? (product.commission_rate * 100).toString() : "",
        sizes: product.sizes || [""],
        size_prices: product.size_prices || {}
      });
    } else {
      // Creating new product
      setFormData({
        name: "",
        brand: "",
        commission_rate: "",
        sizes: [""],
        size_prices: {}
      });
    }
    setErrors({});
  }, [product, open]);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Product name is required";
    } else if (formData.name.length < 2 || formData.name.length > 100) {
      newErrors.name = "Name must be between 2 and 100 characters";
    }

    if (!formData.brand.trim()) {
      newErrors.brand = "Brand is required";
    } else if (formData.brand.length < 2 || formData.brand.length > 100) {
      newErrors.brand = "Brand must be between 2 and 100 characters";
    }

    if (formData.commission_rate) {
      const rate = parseFloat(formData.commission_rate);
      if (isNaN(rate) || rate < 0 || rate > 100) {
        newErrors.commission_rate = "Commission rate must be between 0 and 100";
      }
    }

    const validSizes = formData.sizes.filter(s => s.trim());
    if (validSizes.length === 0) {
      newErrors.sizes = "At least one size is required";
    }

    // Check that all sizes have prices
    validSizes.forEach(size => {
      const price = formData.size_prices[size];
      if (!price || parseFloat(price) <= 0) {
        newErrors[`price_${size}`] = `Price for ${size} must be greater than 0`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    const validSizes = formData.sizes.filter(s => s.trim());
    const sizePrices = {};
    validSizes.forEach(size => {
      sizePrices[size] = parseFloat(formData.size_prices[size]);
    });

    const payload = {
      name: formData.name.trim(),
      brand: formData.brand.trim(),
      sizes: validSizes,
      size_prices: sizePrices,
    };

    // Only include commission_rate if it's explicitly set
    if (formData.commission_rate) {
      payload.commission_rate = parseFloat(formData.commission_rate) / 100;
    }

    onSave(payload);
  };

  const addSize = () => {
    setFormData(prev => ({
      ...prev,
      sizes: [...prev.sizes, ""]
    }));
  };

  const removeSize = (index) => {
    const sizeToRemove = formData.sizes[index];
    const newSizes = formData.sizes.filter((_, i) => i !== index);
    const newSizePrices = { ...formData.size_prices };
    delete newSizePrices[sizeToRemove];
    
    setFormData(prev => ({
      ...prev,
      sizes: newSizes,
      size_prices: newSizePrices
    }));
  };

  const updateSize = (index, value) => {
    const oldSize = formData.sizes[index];
    const newSizes = [...formData.sizes];
    newSizes[index] = value;

    // If size name changed and had a price, move the price to new size name
    const newSizePrices = { ...formData.size_prices };
    if (oldSize && oldSize !== value && newSizePrices[oldSize]) {
      newSizePrices[value] = newSizePrices[oldSize];
      delete newSizePrices[oldSize];
    }

    setFormData(prev => ({
      ...prev,
      sizes: newSizes,
      size_prices: newSizePrices
    }));
  };

  const updatePrice = (size, value) => {
    setFormData(prev => ({
      ...prev,
      size_prices: {
        ...prev.size_prices,
        [size]: value
      }
    }));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {product ? "Edit Product" : "Add New Product"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={submitting}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={submitting}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Brand */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Brand *
            </label>
            <input
              type="text"
              value={formData.brand}
              onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.brand ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={submitting}
            />
            {errors.brand && (
              <p className="text-red-500 text-sm mt-1">{errors.brand}</p>
            )}
          </div>

          {/* Commission Rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Commission Rate (%)
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={formData.commission_rate}
              onChange={(e) => setFormData(prev => ({ ...prev, commission_rate: e.target.value }))}
              placeholder="Leave empty to use system default"
              className={`w-full px-3 py-2 border rounded-md ${
                errors.commission_rate ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={submitting}
            />
            {errors.commission_rate && (
              <p className="text-red-500 text-sm mt-1">{errors.commission_rate}</p>
            )}
            {!formData.commission_rate && !errors.commission_rate && (
              <p className="text-gray-500 text-xs mt-1">If left empty, the system default commission rate will be used</p>
            )}
          </div>

          {/* Sizes and Prices */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Sizes & Prices *
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addSize}
                disabled={submitting}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Size
              </Button>
            </div>
            {errors.sizes && (
              <p className="text-red-500 text-sm mb-2">{errors.sizes}</p>
            )}
            <div className="space-y-2">
              {formData.sizes.map((size, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Size (e.g., 500g, 1kg)"
                    value={size}
                    onChange={(e) => updateSize(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    disabled={submitting}
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Price (₹)"
                    value={formData.size_prices[size] || ""}
                    onChange={(e) => updatePrice(size, e.target.value)}
                    className={`flex-1 px-3 py-2 border rounded-md ${
                      errors[`price_${size}`] ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={submitting || !size.trim()}
                  />
                  {formData.sizes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSize(index)}
                      className="text-red-500 hover:text-red-700"
                      disabled={submitting}
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t">
            {product && onDelete && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (window.confirm("Are you sure you want to delete this product? This action cannot be undone.")) {
                    onDelete(product.id);
                  }
                }}
                disabled={submitting || deleting}
                className="bg-red-600 hover:bg-red-700 text-white border-red-600"
              >
                {deleting ? "Deleting..." : "Delete Product"}
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={submitting || deleting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting || deleting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {submitting ? "Saving..." : product ? "Update Product" : "Create Product"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

